import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import {
  attributePaidAcrossLinks,
  depositAttributed,
  linksOutstanding,
} from './payment-lines.util';

/**
 * Shared source of a booking's extra-item links with `data.paid` reconciled —
 * the same model payment-information uses. RWRSPI-1026 bookings carry real
 * per-line stamps and pass through unchanged; legacy bookings (lines never
 * stamped at booking time) get the purchase-payments history
 * total attributed across in-memory copies, so flows that charge "the
 * outstanding balance" (pay balance, reschedule, extend) never re-charge the
 * deposit or historic instalments.
 */
@Injectable({ providedIn: 'root' })
export class BalanceService {
  constructor(private apiService: ApiService) {}

  /** serviceId is the booked service's id (not global_id) — it feeds the purchase-payments lookup. */
  async getReconciledLinks(booking: any, serviceId: any): Promise<any[]> {
    const { links } = await this.getReconciledLinksDetailed(booking, serviceId);
    return links;
  }

  /** As getReconciledLinks, plus whether the booking was on the legacy (unattributed) model. */
  async getReconciledLinksDetailed(
    booking: any,
    serviceId: any,
  ): Promise<{ links: any[]; wasLegacy: boolean }> {
    let links: any[] = [];
    try {
      const res = await this.apiService.getServiceItemExtraLinksForBooking(
        booking.company_id,
        booking.id,
      ) as any;
      links = Array.isArray(res) ? res : [];
    } catch (err) {
      console.error('Failed to fetch extra-item links for balance reconciliation', err);
      return { links: [], wasLegacy: false };
    }

    if (depositAttributed(links)) {
      return { links, wasLegacy: false };
    }

    let historyTotal = 0;
    try {
      const history: any = await this.apiService.getPurchasePayments(
        booking.company_id,
        serviceId,
        booking.purchase_id,
      );
      for (const p of history?._embedded?.payments ?? []) {
        historyTotal += Number(p.amount) || 0;
      }
    } catch (err) {
      console.error('Failed to fetch purchase payments for balance reconciliation', err);
    }
    return { links: attributePaidAcrossLinks(links, historyTotal), wasLegacy: true };
  }

  /** Convenience: reconciled links + their total unpaid remainder in pounds. */
  async getOutstanding(booking: any, serviceId: any): Promise<{ links: any[]; outstanding: number }> {
    const links = await this.getReconciledLinks(booking, serviceId);
    return { links, outstanding: linksOutstanding(links) };
  }
}
